import { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { AMBER, NAVY, BORDER_COLOR } from '../../styles/tokens';

/**
 * Multi-select status filter dropdown (replaces FilterPills for Gastos pages).
 * Props:
 *   options: [{ value, label }]
 *   active: Set<string> of selected values (empty = all)
 *   onChange: (newSet: Set<string>) => void
 */
export default function StatusFilterDropdown({ options, active, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filteredOptions = options.filter(o => o.value !== 'all');
  const activeCount = active.size;
  const label = activeCount === 0
    ? 'Todos los estados'
    : activeCount === 1
      ? filteredOptions.find(o => active.has(o.value))?.label || 'Filtrado'
      : `${activeCount} estados`;

  function toggle(value) {
    const next = new Set(active);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(next);
  }

  function clearAll() {
    onChange(new Set());
  }

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 14px', borderRadius: 20,
          border: `1.5px solid ${activeCount > 0 ? AMBER : BORDER_COLOR}`,
          background: activeCount > 0 ? AMBER : '#fff',
          color: activeCount > 0 ? '#fff' : '#475569',
          fontWeight: activeCount > 0 ? '600' : '500',
          fontSize: 13, cursor: 'pointer',
          fontFamily: 'Inter, sans-serif',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
        <ChevronDown size={14} style={{ opacity: 0.6 }} />
      </button>

      {activeCount > 0 && (
        <button
          onClick={clearAll}
          style={{
            position: 'absolute', top: -6, right: -6,
            width: 18, height: 18, borderRadius: '50%',
            background: '#ef4444', color: '#fff', border: 'none',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 0,
          }}
        >
          <X size={10} />
        </button>
      )}

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4,
          background: '#fff', border: `1px solid ${BORDER_COLOR}`,
          borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 100, minWidth: 200, padding: '6px 0',
        }}>
          {filteredOptions.map(opt => {
            const checked = active.has(opt.value);
            return (
              <label
                key={opt.value}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 14px', cursor: 'pointer', fontSize: 13,
                  fontFamily: 'Inter, sans-serif',
                  background: checked ? '#fffbeb' : 'transparent',
                  color: checked ? NAVY : '#475569',
                  fontWeight: checked ? 600 : 400,
                }}
                onMouseEnter={e => { if (!checked) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { if (!checked) e.currentTarget.style.background = 'transparent'; }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(opt.value)}
                  style={{ accentColor: AMBER }}
                />
                {opt.label}
              </label>
            );
          })}
          {activeCount > 0 && (
            <div style={{ borderTop: `1px solid ${BORDER_COLOR}`, padding: '6px 14px' }}>
              <button
                onClick={clearAll}
                style={{
                  background: 'none', border: 'none', color: '#64748b',
                  fontSize: 12, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                }}
              >
                Limpiar filtros
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
